import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useLabels, useRequestData } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';
import { isBrowser } from 'ws-scripts/modules/environment';
import { useCompareVehiclesList } from '../../../../hooks/useCompareVehicles';
import {
	addCompareVehicle,
	removeCompareVehicle
} from '../../../../features/compare';
import { getVehicleObj } from '../../../../utilities/vehicleTracking';
import './CompareIconButton.scss';

const CompareIconButton = ({ uuid }) => {
	const dispatch = useDispatch();
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const compareVehicles = useCompareVehiclesList();
	const selected = compareVehicles.includes(uuid);

	const icon = selected ? 'ddc-icon-check' : 'ddc-icon-expand';
	const label = selected ? labels.get('REMOVE') : labels.get('COMPARE');

	const buttonSelectedClasses = ['compare-icon-button-selected'];

	const buttonUnselectedClasses = ['compare-icon-button-unselected'];

	const buttonClasses = [
		'compare-icon-button',
		'transition-duration-xl',
		'text-decoration-none',
		'overflow-hidden',
		'd-inline-flex',
		'align-items-center',
		'flex-row-reverse',
		...(selected ? buttonSelectedClasses : buttonUnselectedClasses)
	];

	const handleClick = (e) => {
		const newSelected = !selected;
		const vehicleObj = getVehicleObj(uuid);

		if (isBrowser && window.DDC?.pushToGtmDataLayer) {
			const eventData = {
				isSRP: true,
				checked: newSelected,
				element: e.target,
				...vehicleObj
			};
			delete eventData.condition;
			window.DDC.pushToGtmDataLayer({
				event: 'ddc.link.vehicleCompareCheck',
				eventData
			});
		}

		dispatch(
			newSelected ? addCompareVehicle(uuid) : removeCompareVehicle(uuid)
		);
		trackEvent(
			widgetName,
			windowId,
			newSelected
				? {
						element: 'Compare selection',
						result: 'Vehicle selected for comparison',
						vehicle: vehicleObj
				  }
				: {
						element: 'Deselect comparison selection link',
						result: 'Vehicle removed from comparison'
				  }
		);
	};

	return (
		<div className="compare-icon-button-container position-absolute">
			<button
				type="button"
				className={setClassNames(buttonClasses)}
				id={`${uuid}-compare`}
				aria-label={label}
				aria-pressed={selected}
				onClick={handleClick}
			>
				<span
					className={setClassNames([
						'compare-icon-button-icon',
						'ddc-icon',
						'icon-size-3',
						icon
					])}
					aria-hidden="true"
				/>
				<span className="compare-icon-button-text ddc-font-size-xsmall px-2 text-nowrap">
					{label}
				</span>
			</button>
		</div>
	);
};

CompareIconButton.propTypes = {
	uuid: PropTypes.string.isRequired
};

export default CompareIconButton;
